import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {POLICY_TEMPLATE_FRAGMENT} from 'auth/PolicyConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import * as RegistryUtil from 'registry/RegistryUtil';

import {
  UserRegistriesListingQuery,
  UserRegistriesListingQueryVariables,
} from './__generated/UserRegistriesListingQuery';
import PoliceablePolicyTable, {
  Policeable,
  POLICY_TABLE_PARTY_POLICEABLE_LISTING_FRAGMENT,
} from './PoliceablePolicyTable';
import useLimitOffset from './usePoliceablePolicyTableLimitOffset';

export interface RegistryPolicyTableProps {
  userId: string;
}

export const USER_REGISTRIES_LISTING_QUERY = gql`
  query UserRegistriesListingQuery($userId: StrictString!, $limitOffsetInput: LimitOffsetInput!)
  @skipAudit
  @engComponent(name: IDENTITY) {
    user(id: $userId) {
      ... on UserNode {
        id
        userRegistriesListing(limitOffsetInput: $limitOffsetInput) {
          ... on PartyPoliceablesListingNode {
            ...PolicyTablePartyPoliceableListing
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
    policyTemplate(policyType: DATA) {
      ...PolicyTemplate
    }
  }
  ${POLICY_TABLE_PARTY_POLICEABLE_LISTING_FRAGMENT}
  ${POLICY_TEMPLATE_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const principalType = 'user';

export default function RegistryPolicyTable(props: RegistryPolicyTableProps): JSX.Element | null {
  const [{limit, offset}, {setLimit, setOffset}] = useLimitOffset();

  const result = useBenchlingQuery<UserRegistriesListingQuery, UserRegistriesListingQueryVariables>(
    USER_REGISTRIES_LISTING_QUERY,
    {
      variables: {
        userId: props.userId,
        limitOffsetInput: {
          limit,
          offset,
        },
      },
      fetchPolicy: 'network-only',
    }
  );

  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load projects; try reloading the page.');
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return (
      <PoliceablePolicyTable
        isLoading={true}
        resourceType="project"
        setOffset={setOffset}
        setLimit={setLimit}
        pagination={{offset, limit, total: 0}}
        principalType={principalType}
      />
    );
  } else {
    assert(
      !isInaccessible(result.data.user),
      `Expected ${principalType} currently being viewed to be readable`
    );
    return (
      <PoliceablePolicyTable
        isLoading={false}
        resourceType="registry"
        icon={ModelTypeConstants.REGISTRY}
        setOffset={setOffset}
        setLimit={setLimit}
        pagination={{offset, limit, total: result.data.user.userRegistriesListing.total}}
        policyTemplate={result.data.policyTemplate}
        getPoliceableDisplayName={(policeable: Policeable) => {
          assert(policeable.__typename === 'RegistryNode', 'Expected RegistryNode');
          return `${policeable.ownerJson.name} ${policeable.name}`;
        }}
        partyPoliceablesListing={result.data.user.userRegistriesListing}
        openPoliceablePage={(policeable: Policeable) => {
          assert(policeable.__typename === 'RegistryNode', 'Expected RegistryNode');
          navigateToURL(policeable.url);
        }}
        openPoliceableSettingsPage={(policeable: Policeable) => {
          assert(policeable.__typename === 'RegistryNode', 'Expected RegistryNode');
          navigateToURL(
            RegistryUtil.getRegistryGeneralSettingsUrl(policeable.ownerJson.url_identifier, policeable.id)
          );
        }}
        principalType={principalType}
      />
    );
  }
}
